import { useState } from 'react';
import { Card, CardTitle, CardDescription } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { useUsage } from '@/lib/queries';
import { cn } from '@/lib/utils';

const selectClass = cn(
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11',
  'font-(family-name:--font-body) focus:outline-2 focus:outline-offset-2 focus:outline-accent'
);

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function lastNMonths(n: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

const fmtUsd = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const fmtNum = new Intl.NumberFormat();

export default function Usage() {
  const [month, setMonth] = useState(currentMonth);
  const { data, isLoading, error } = useUsage(month);
  const months = lastNMonths(12);

  return (
    <>
      <Card>
        <CardTitle>API Usage</CardTitle>
        <CardDescription className="mt-2">
          Monthly provider spend and call volume. Bright Data, Decodo, Claude.
        </CardDescription>
        <label className="flex items-center gap-2 mt-4">
          <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">Month</span>
          <select className={selectClass} value={month} onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
      </Card>

      {isLoading && <LoadingSkeleton lines={4} />}
      {error && <ErrorState message={(error as Error).message} />}

      {data && (
        <>
          <Card>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <Stat label="Total cost" value={fmtUsd.format(data.totalCostUsd)} />
              <Stat label="API calls" value={fmtNum.format(data.totalCalls)} />
              <Stat label="Providers" value={String(data.byProvider.length)} />
            </div>
          </Card>

          {data.byProvider.length === 0 ? (
            <Card>
              <CardDescription>No API usage recorded for {month}.</CardDescription>
            </Card>
          ) : (
            <Card>
              <h2 className="text-xl font-semibold mb-4">By provider</h2>
              {/* Desktop */}
              <table className="hidden md:table w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-accent">
                    <Th>Provider</Th>
                    <Th right>Calls</Th>
                    <Th right>Cost (USD)</Th>
                    <Th right>Tokens in</Th>
                    <Th right>Tokens out</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.byProvider.map((r) => (
                    <tr key={r.provider} className="border-b border-border hover:bg-surface-hover">
                      <td className="py-3 px-3 font-medium">{r.provider}</td>
                      <td className="py-3 px-3 text-right tabular-nums">{fmtNum.format(r.calls)}</td>
                      <td className="py-3 px-3 text-right tabular-nums">{fmtUsd.format(r.costUsd)}</td>
                      <td className="py-3 px-3 text-right tabular-nums">{fmtNum.format(r.tokensIn)}</td>
                      <td className="py-3 px-3 text-right tabular-nums">{fmtNum.format(r.tokensOut)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Mobile */}
              <ul className="md:hidden flex flex-col divide-y divide-border">
                {data.byProvider.map((r) => (
                  <li key={r.provider} className="py-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium">{r.provider}</span>
                      <span className="tabular-nums text-accent font-medium">{fmtUsd.format(r.costUsd)}</span>
                    </div>
                    <div className="text-[0.75rem] text-muted mt-1 flex flex-wrap gap-x-3 tabular-nums">
                      <span>{fmtNum.format(r.calls)} calls</span>
                      <span>· {fmtNum.format(r.tokensIn)} in</span>
                      <span>· {fmtNum.format(r.tokensOut)} out</span>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">{label}</div>
      <div className="text-2xl font-(family-name:--font-heading) font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui) ${right ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}
